﻿import {translation} from "@2d-front-monorepo/core";
import {AsyncInitializer, DefaultErrorFallback} from "@2d-front-monorepo/design-library";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, useCallback} from "react";
import {ErrorBoundary} from "react-error-boundary";
import {Route, Routes} from "react-router";
import NavigateToDefault from "./components/NavigateToDefault";
import PermitsLoading from "./components/PermitsLoading";
import PrivateRoute from "./components/PrivateRoute";
import {GlobalContext} from "./contexts/GlobalContext";
import {UserProvider} from "./contexts/UserContext";
import {permitsLogErrorToServer} from "./functions";
import {MainLayout} from "./layouts/MainLayout";
import {MenuLayout} from "./layouts/MenuLayout";
import ForgotPassword from "./pages/auth/ForgotPassword";
import SignIn from "./pages/auth/SignIn";
import SignUp from "./pages/auth/SignUp";
import Documents from "./pages/documents/Documents";
import DocumentUpdate from "./pages/documents/DocumentUpdate";
import ExpiredDocuments from "./pages/documents/ExpiredDocuments";
import CreateDriver from "./pages/drivers/CreateDriver";
import ViewDriver from "./pages/drivers/ViewDriver";
import Invoices from "./pages/invoices/Invoices";
import NotFound from "./pages/NotFound";
import Payments from "./pages/payments/Payments";
import ApproveReservation from "./pages/permits/ApproveReservation";
import PermitsGroup from "./pages/permits/Groups";
import Home from "./pages/permits/Home";
import PermitList from "./pages/permits/PermitList";
import RenewPermit from "./pages/permits/RenewPermit";
import UnparkPermit from "./pages/permits/UnparkPermit";
import UpdateDocumentation from "./pages/permits/UpdateDocumentation";
import ViewPermit from "./pages/permits/ViewPermit";
import ViewReservation from "./pages/permits/ViewReservation";
import CreateResidency from "./pages/residencies/CreateResidency";
import ViewResidency from "./pages/residencies/ViewResidency";
import CompleteProfile from "./pages/settings/CompleteProfile";
import Settings from "./pages/settings/Settings";
import CreateVehicle from "./pages/vehicles/CreateVehicle";
import EditVehicle from "./pages/vehicles/EditVehicle";
import Vehicles from "./pages/vehicles/Vehicles";
import ViewVehicle from "./pages/vehicles/ViewVehicle";
import Welcome from "./pages/Welcome";
import {getWelcomeRoutes} from "./routes";
import {APP_VERSION} from "./version";

console.info(`%cPermits-UI - Version ${APP_VERSION}`, "color: blue;");

export default function Application(): JSX.Element {

	const restartApplication = useCallback(() => {
		location.reload();
	}, []);

	return (
		<ErrorBoundary FallbackComponent={DefaultErrorFallback} onReset={restartApplication} onError={permitsLogErrorToServer}>
			<AsyncInitializer initializer={GlobalContext}
			                  loading={<PermitsLoading title={translation(PermitTranslations.website__application_loading)}/>}>
				<UserProvider>
					<Routes>
						<Route path="/" element={<MainLayout/>}>
							<Route index element={<NavigateToDefault/>}/>
							<Route path="welcome" element={<Welcome/>}/>

							<Route path="auth">
								<Route path="signin" element={<SignIn/>}/>
								<Route path="signup" element={<SignUp/>}/>
								<Route path="forgot-password" element={<ForgotPassword/>}/>
							</Route>

							<Route element={<PrivateRoute/>}>
								<Route path="complete-profile" element={<CompleteProfile/>}/>

								<Route element={<MenuLayout/>}>
									<Route path="home/*" element={<Home/>}/>

									<Route path="permits">
										<Route index element={<PermitList/>}/>
										<Route path="groups/:groupName" element={<PermitsGroup/>}/>
										<Route path="renew/:permitId" element={<RenewPermit/>}/>
										<Route path="unpark/:permitId" element={<UnparkPermit/>}/>
										<Route path=":permitId" element={<ViewPermit/>}/>
										<Route path=":permitId/documentation-update" element={<UpdateDocumentation/>}/>
									</Route>

									<Route path="reservations">
										<Route path=":reservationId" element={<ViewReservation/>}/>
										<Route path=":reservationId/approve" element={<ApproveReservation/>}/>
									</Route>

									<Route path="drivers">
										<Route path="create" element={<CreateDriver/>}/>
										<Route path=":driverId" element={<ViewDriver/>}/>
									</Route>

									<Route path="residencies">
										<Route path="create" element={<CreateResidency/>}/>
										<Route path=":residencyId" element={<ViewResidency/>}/>
									</Route>

									<Route path="vehicles">
										<Route index element={<Vehicles/>}/>
										<Route path="create" element={<CreateVehicle/>}/>
										<Route path=":vehicleId/edit" element={<EditVehicle/>}/>
										<Route path=":vehicleId" element={<ViewVehicle/>}/>
									</Route>

									<Route path="payments" element={<Payments/>}/>

									<Route path="documents">
										<Route index element={<Documents/>}/>
										<Route path="expired" element={<ExpiredDocuments/>}/>
										<Route path=":documentId/update" element={<DocumentUpdate/>}/>
									</Route>

									<Route path="invoices" element={<Invoices/>}/>

									<Route path="settings" element={<Settings/>}/>
								</Route>
							</Route>

							{getWelcomeRoutes().map(route => (
								<Route key={route.path} path={route.path} element={route.component}/>
							))}
						</Route>
						<Route path="*" element={<NotFound/>}/>
					</Routes>
				</UserProvider>
			</AsyncInitializer>
		</ErrorBoundary>
	)
}
